import React from 'react';

import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';

import AdminApi from './AdminApi';
import { CartContext } from '../cart/CartHandler';

/** UcDxfExport::FORMATS, as the menu offers them. */
const FORMATS: { value: string; label: string }[] = [
    { value: 'dxf', label: 'DXF 2D' },
    { value: 'dxf3d', label: 'DXF 3D' },
    { value: 'dxf3d-scm', label: 'DXF 3D - SCM Maestro' },
    { value: 'dxf3d-alphacam', label: 'DXF 3D - Alphacam' },
    { value: 'xcs', label: 'XCS - N100 nesting + CY900 (PP_Report.csv)' },
];

/**
 * Downloads the design the Designer shows as a zip of its parts' manufacturing files, in the
 * format picked from its menu: each part's raw (CNC cut) and finished (banded) stage. The
 * employee-gated ucadmin `dxfZip` action makes them from the order Unity built (cabinetResult).
 * Used by the admin panel and the AI designer page.
 */
const DxfExportButton: React.FC = () => {
    const { cabinetResult } = React.useContext(CartContext);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);

    const download = async (format: string) => {
        setAnchor(null);
        if (!cabinetResult) return;
        setBusy(true);
        setError(null);
        const result = await AdminApi.dxfZip(cabinetResult, format);
        setBusy(false);
        if ('error' in result) {
            setError(result.error);
            return;
        }
        const url = URL.createObjectURL(result.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.fileName;
        link.click();
        // revoked a moment later: right after click() some browsers would drop the download
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    return (
        <>
            <Tooltip title={error ?? 'A látott terv minden lapja gyártási fájlként (zip): nyers és kész méret'}>
                <span>
                    <Button size="small" color={error ? 'error' : 'primary'} disabled={!cabinetResult || busy}
                        startIcon={busy ? <CircularProgress size={14} /> : <DownloadIcon />}
                        onClick={(event) => setAnchor(event.currentTarget)}>
                        DXF / XCS
                    </Button>
                </span>
            </Tooltip>
            <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
                {FORMATS.map((format) => (
                    <MenuItem key={format.value} dense onClick={() => download(format.value)}>
                        {format.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default DxfExportButton;
